import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'

import Tooltip from '../../../../atoms/Tooltip'
import AcceptRejectV2 from '../../AcceptRejectV2'
import NewCount from '../../../../molecules/BaseConversation/NewCount'
import TypingIndicator from '../../../../atoms/TypingIndicator'

import {
  findFilterID,
  getLatestEvent,
  formatLastMessage
} from '../../../../../utilities'
import { selectConvo, updateCache } from '../../../../../slices/convo'
import { useAgent } from '../../../../../providers/AgentProvider'
import { useNotificationCount } from '../../../../../providers/NotificationCountProvider'
import { useReadStatus } from '../../../../../providers/ReadStatusProvider'
import { useQueueState } from '../../../../../providers/QueueStateProvider'
import { useTyping } from '../../../../../providers/TypingProvider'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { selectViews } from '../../../../../slices/views'
import useStyles from './styles'

const ContentArea = ({ conversation, related }) => {
  const {
    users,
    session,
    messages,
    isNew,
    state,
    owner_id: ownerID,
    latest_activity: latestActivity,
    assigned_to
  } = conversation

  const dispatch = useAppDispatch()

  const { convos } = useReadStatus()
  const { getTyping } = useTyping()
  const { id: queueSelected } = useQueueState()
  const { currentUser } = useAgent()
  const { updateCount } = useNotificationCount()
  const { allUsers, loadUser, getUsers } = useUserAPI()
  const { view } = useAppSelector(selectConvo)
  const { views } = useAppSelector(selectViews)

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  const isViewing = React.useMemo(
    () => session && sessionStorage.getItem('viewing') === session,
    [session]
  )
  const isYours = React.useMemo(
    () => users?.includes(currentUser()?.id) || isViewing,
    [users, currentUser, isViewing]
  )
  const pickedUp = React.useMemo(
    () =>
      isYours ||
      ['REJECTED', 'CLOSED', 'ENDED'].includes(state) ||
      assigned_to !== null,
    [isYours, state, assigned_to]
  )

  const isSelected = React.useMemo(
    () => session === queueSelected,
    [session, queueSelected]
  )

  // Gets list of typing people
  const typingNames = React.useMemo(
    () => getTyping(session),
    [getTyping, session]
  )

  // Gets number of new messages
  const newCount = React.useMemo(() => {
    if (isSelected) {
      return 0
    }

    return updateCount(session, convos, messages)
  }, [isSelected, updateCount, session, convos, messages])

  const hasUnreadMessages = React.useMemo(
    () => newCount > 0 || isNew,
    [newCount, isNew]
  )
  const canShowCount = React.useMemo(
    () => hasUnreadMessages && pickedUp,
    [hasUnreadMessages, pickedUp]
  )

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const latests = []

    related.forEach(obj => {
      const filter = findFilterID(ownerID, false, obj.session)
      const latest = getLatestEvent(
        {
          ...obj,
          latestActivity
        },
        filter
      )
      latests.push(latest)
    })

    if (!latests.length) {
      return null
    }

    return latests.sort((a, b) => +new Date(b.date) - +new Date(a.date))[0]
  }, [related, ownerID, latestActivity])

  React.useEffect(() => {
    loadUser(lastMessage?.meta?.by_user)

    // Load users mentioned in the message
    if (typeof lastMessage?.content === 'string') {
      const matches =
        lastMessage.content.match(
          /\|@[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\|/gm
        ) ?? []
      matches.forEach(id => {
        loadUser(id.slice(2, -1))
      })
    }
  }, [loadUser, lastMessage])

  const lastText = React.useMemo(
    () => formatLastMessage(lastMessage, allAgents, allUsers, currentUser),
    [lastMessage, allAgents, allUsers, currentUser]
  )

  // Store unreads in cache
  React.useEffect(() => {
    const count = canShowCount ? newCount : 0
    const selectedView = views.find(v => v.id === view) ?? null
    dispatch(
      updateCache({
        id: session,
        count,
        userID: currentUser()?.id ?? '',
        view: selectedView
      })
    )
  }, [canShowCount, newCount, session, currentUser, dispatch, view, views])

  const classes = useStyles({ hasUnreadMessages })

  return (
    <div className={classes.root}>
      <div className={classes.messageContainer}>
        {typingNames.length > 0 ? (
          <TypingIndicator names={typingNames} numUsers={users.length} />
        ) : (
          <Tooltip arrow title={lastText}>
            <div
              className={clsx(
                classes.message,
                !pickedUp && classes.messageSingleLine
              )}
            >
              {lastText}
            </div>
          </Tooltip>
        )}

        {canShowCount && (
          <div className={classes.count}>
            <NewCount count={newCount} dontGroup />
          </div>
        )}
      </div>

      {!pickedUp && <AcceptRejectV2 conversation={conversation} />}
    </div>
  )
}

export default ContentArea
